import { TEST_ID_SIMILAR_PLAYER } from "e2e/testId";
import { CheckIcon, LucideProps, PlusIcon } from "lucide-react-native";
import React, { memo } from "react";
// design-system: rn-primitive waiver — onStartShouldSetResponder is an RN View responder API absent from the web Box atom
// eslint-disable-next-line no-restricted-imports -- RFC-#6 genuine-runtime: inner View uses onStartShouldSetResponder, an RN View responder API absent from the Box atom's web public type…
import { View } from "react-native";

import { TouchableWithoutFeedback } from "react-native-gesture-handler";

import { CustomPromptRefProps } from "@/components/molecules/CustomPrompt/CustomPromptContainer";
import { IInfoBadge } from "@/components/organisms/InfoPlayer/InfoPlayerView";

import AddToWatchlistActionsheet from "@/components/organisms/AddToWatchlistActionsheet";
import BadgeComponent from "@/components/molecules/Badge";
import ManualPlayerBadge from "@/components/molecules/ManualPlayerBadge";
import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import {
  CurrencyCode,
  PlayerSearchPlayerFragment,
  Position,
  SimilarPlayerSortField,
} from "@/generated/graphql";
import { IMenuSelection } from "@/stores/selectionSort";
import { getPositionAndTeamName } from "@/utils/getPlayerInformations";
import { RADIUS } from "@/config/radius";

// design-system: dynamic-style waiver — theme + state driven background (watchlist membership / dark mode)
const addButtonStyle = (isPlayerInAnyWatchlist: boolean, isDark: boolean) => ({
  marginRight: 8,
  padding: 4,
  backgroundColor: isPlayerInAnyWatchlist
    ? "#0b8dcd"
    : isDark
      ? "#26252A"
      : "#e5e5e5",
  borderRadius: RADIUS.full,
  justifyContent: "center" as const,
  alignItems: "center" as const,
});

// design-system: runtime-value waiver — state-driven icon color (watchlist membership)
const addIconColor = (isPlayerInAnyWatchlist: boolean) =>
  isPlayerInAnyWatchlist ? "#ffffff" : "#0b8dcd";

/** Add-button icon size in px (matches the previous Icon atom `size="xl"` → h-6 w-6). */
const ADD_ICON_SIZE = 24;

/**
 * Props for SearchResultsItemView component
 */
interface Props {
  item: PlayerSearchPlayerFragment;
  playerName: string;
  showActionsheet: boolean;
  onClose: () => void;
  onAddPress: () => void;
  onAddWatchlistPress: () => void;
  loading: boolean;
  isPlayerInAnyWatchlist: boolean;
  score?: number | null;
  similarityShowsSetting?: IMenuSelection;
  onPress: () => void;
  onStartShouldSetResponder: () => boolean;
  promptRef?: React.RefObject<CustomPromptRefProps>;
  infoBadge?: IInfoBadge;
  currencyCode: CurrencyCode;
  isDark: boolean;
}

/**
 * Pure view component for search results item
 * @param root0 - Component props
 * @param root0.item - Player search data
 * @param root0.playerName - Player display name
 * @param root0.showActionsheet - Whether actionsheet is shown
 * @param root0.onClose - Handler for close action
 * @param root0.onAddPress - Handler for add press
 * @param root0.onAddWatchlistPress - Handler for add to watchlist
 * @param root0.loading - Whether data is loading
 * @param root0.isPlayerInAnyWatchlist - Whether player is in any watchlist
 * @param root0.score - Similarity score
 * @param root0.onPress - Handler for item press
 * @param root0.onStartShouldSetResponder - Handler for responder events
 * @param root0.promptRef - Reference to custom prompt
 * @param root0.similarityShowsSetting - Similarity display settings
 * @param root0.infoBadge - Badge configuration
 * @param root0.currencyCode - Currency code for formatting
 * @param root0.isDark - Whether dark theme is active
 */
const SearchResultsItemView = ({
  item,
  playerName,
  showActionsheet,
  onClose,
  onAddPress,
  onAddWatchlistPress,
  loading,
  isPlayerInAnyWatchlist,
  score,
  onPress,
  onStartShouldSetResponder,
  promptRef,
  similarityShowsSetting,
  infoBadge,
  currencyCode,
  isDark,
}: Props) => (
  <Box className="min-h-20 py-2">
    <TouchableWithoutFeedback
      testID={TEST_ID_SIMILAR_PLAYER.PLAYER_RESULT_ITEM}
      onPress={onPress}
      disabled={loading}
    >
      <View
        onStartShouldSetResponder={onStartShouldSetResponder}
        className="flex-row items-center gap-4"
      >
        <TouchableWithoutFeedback
          testID={TEST_ID_SIMILAR_PLAYER.ADD_TO_LIST_BUTTON}
          // eslint-disable-next-line no-restricted-syntax -- gesture-handler TouchableWithoutFeedback (non-atomizable) with data-driven background (watchlist membership / theme)
          style={addButtonStyle(isPlayerInAnyWatchlist, isDark)}
          onPress={loading ? undefined : onAddPress}
        >
          {isPlayerInAnyWatchlist ? (
            <CheckIcon
              size={ADD_ICON_SIZE}
              {...({
                color: addIconColor(isPlayerInAnyWatchlist),
                strokeWidth: 3,
              } as Partial<LucideProps>)}
            />
          ) : (
            <PlusIcon
              size={ADD_ICON_SIZE}
              {...({
                color: addIconColor(isPlayerInAnyWatchlist),
                strokeWidth: 3,
              } as Partial<LucideProps>)}
            />
          )}
        </TouchableWithoutFeedback>
        <Stack className="flex-1">
          <Stack direction="horizontal" className="items-center gap-2">
            <Text
              variant="title-lg"
              className={isDark ? "text-white" : "text-content-primary"}
            >
              {playerName}
            </Text>
            <ManualPlayerBadge show={item?.isManual ?? false} compact />
          </Stack>
          <Text
            variant="unstyled"
            className={`font-medium text-title ${isDark ? "text-content-subtle" : "text-content-neutral-strong"}`}
          >
            {getPositionAndTeamName(
              item?.positions as Position[],
              item?.team?.name ?? item?.teamName ?? undefined,
              loading
            )}
          </Text>
        </Stack>
        <Stack direction="horizontal" className="items-start gap-2">
          {score && score < 100 ? (
            <>
              {similarityShowsSetting?.type ===
                SimilarPlayerSortField.SimilarityScore && (
                <BadgeComponent
                  value={score}
                  min={40}
                  max={100}
                  isHigherBetter
                />
              )}
              {similarityShowsSetting?.type !==
                SimilarPlayerSortField.SimilarityScore &&
                infoBadge && (
                  <BadgeComponent
                    type={infoBadge?.type}
                    value={infoBadge.value === -1 ? null : infoBadge.value}
                    min={infoBadge.min}
                    max={infoBadge.max}
                    currencyCode={currencyCode}
                    isHigherBetter
                    isDefaultStyle={infoBadge.isDefaultStyle}
                  />
                )}
            </>
          ) : (
            // Watchlist mode badges
            <Stack className="gap-2">
              {infoBadge && (
                <BadgeComponent
                  type={infoBadge?.type}
                  value={infoBadge.value === -1 ? null : infoBadge.value}
                  min={infoBadge.min}
                  max={infoBadge.max}
                  currencyCode={currencyCode}
                  isHigherBetter
                  isDefaultStyle={infoBadge.isDefaultStyle}
                />
              )}
            </Stack>
          )}
        </Stack>
      </View>
    </TouchableWithoutFeedback>
    {showActionsheet && (
      <AddToWatchlistActionsheet
        item={item}
        showActionsheet={showActionsheet}
        onClose={onClose}
        onAddPress={onAddPress}
        onAddWatchlistPress={onAddWatchlistPress}
        loading={loading}
        isPlayerInAnyWatchlist={isPlayerInAnyWatchlist}
        promptRef={promptRef}
        isDark={isDark}
      />
    )}
  </Box>
);

SearchResultsItemView.displayName = "SearchResultsItemView";

export default memo(SearchResultsItemView);
